import React, { useEffect, useRef } from 'react';
import './AppliedFilters.scss';
import { Button } from 'primereact/button';
import { PrimeIcons } from 'primereact/api';
import { TableColumn, TableFilterOptions, TableFilters, TableRow, UntypedRow } from '../../types/responsiveTable';
import { appliedFilterText } from './appliedFilterText';

interface AppliedFiltersProps<T extends TableRow = UntypedRow> {
  columns: TableColumn<T>[];
  filters: TableFilters;
  filterOptions?: TableFilterOptions;
  data: T[];
  onRemove: (key: string) => void;
  onClear: () => void;
  /** While true nothing is drawn: the first load of the table. A later request keeps the chips on screen (#417). */
  loading?: boolean;
  /** Called after a chip removal that leaves no chip and no clear link, so the owner can put focus on the button that opens the filters. */
  onFocusLost?: () => void;
}

interface PendingFocus {
  key: string;
  index: number;
}

/**
 * One removable chip per filtered column (in the order of the columns) and a link that clears every filter. Nothing is drawn while
 * no filter is applied. The × of a chip unmounts with it, so after a removal focus goes to the × of the chip that took its place, else
 * the previous chip's ×, else the clear link, else `onFocusLost`.
 */
export const AppliedFilters = <T extends TableRow = UntypedRow>({
  columns,
  filters,
  filterOptions,
  data,
  onRemove,
  onClear,
  onFocusLost,
  loading,
}: AppliedFiltersProps<T>) => {
  const root = useRef<HTMLDivElement>(null);
  const removed = useRef<PendingFocus | null>(null);

  useEffect(() => {
    const pending = removed.current;
    if (!pending || pending.key in filters) return;
    removed.current = null;
    // Focus is only moved while nobody has it (the × that was pressed unmounted): a user who went elsewhere keeps it there
    const focusIsLost = !document.activeElement || document.activeElement === document.body;
    if (!focusIsLost) return;
    const buttons = root.current?.querySelectorAll<HTMLElement>('.applied-filter-remove');
    const target =
      buttons?.[pending.index] ??
      buttons?.[pending.index - 1] ??
      root.current?.querySelector<HTMLElement>('.applied-filters-clear');
    if (target) target.focus();
    else onFocusLost?.();
  });

  if (loading || Object.keys(filters).length === 0) return null;

  return (
    <div className='applied-filters' ref={root} role='group' aria-label='Filtros aplicados'>
      <ul className='applied-filters-list'>
        {columns
          .filter((column) => column.accessor in filters)
          .map((column, index) => {
            const text = `${column.label}: ${appliedFilterText(column, filters[column.accessor], filterOptions, data)}`;
            return (
              <li className='applied-filter-chip' key={column.accessor} title={text}>
                <span className='applied-filter-label'>{text}</span>
                <button
                  type='button'
                  className='applied-filter-remove'
                  aria-label={`Quitar filtro ${column.label}`}
                  onClick={() => {
                    removed.current = { key: column.accessor, index };
                    onRemove(column.accessor);
                  }}
                >
                  <i className={PrimeIcons.TIMES} aria-hidden='true' />
                </button>
              </li>
            );
          })}
      </ul>
      <Button
        label='Limpiar filtros'
        link
        icon={PrimeIcons.FILTER_SLASH}
        iconPos='right'
        onClick={onClear}
        className='applied-filters-clear'
      />
    </div>
  );
};
